import { RouteRecordRaw } from 'vue-router';
/**
 * 建议：路由 path 路径与文件夹名称相同，找文件可浏览器地址找，方便定位文件位置
 *
 * 路由meta对象参数说明
 * meta: {
 *      title:          菜单栏及 tagsView 栏、菜单搜索名称（国际化）
 *      isLink：        是否超链接菜单，开启外链条件，`1、isLink: 链接地址不为空 2、isIframe:false`
 *      isHide：        是否隐藏此路由
 *      isKeepAlive：   是否缓存组件状态
 *      isAffix：       是否固定在 tagsView 栏上
 *      isIframe：      是否内嵌窗口，开启条件，`1、isIframe:true 2、isLink：链接地址不为空`
 *      roles：         当前路由权限标识，取角色管理。控制路由显示、隐藏。超级管理员：admin 普通角色：common
 *      icon：          菜单、tagsView 图标，阿里：加 `iconfont xxx`，fontawesome：加 `fa xxx`
 * }
 */

// 扩展 RouteMeta 接口
declare module 'vue-router' {
	interface RouteMeta {
		title?: string;
		isLink?: string;
		isHide?: boolean;
		isKeepAlive?: boolean;
		isAffix?: boolean;
		isIframe?: boolean;
		roles?: string[];
		icon?: string;
		isManage?: boolean;
		isLogo?: boolean;
	}
}

/**
 * 定义动态路由
 * 前端添加路由，请在顶级节点的 `children 数组` 里添加
 * @description 未开启 isRequestRoutes 为 true 时使用（前端控制路由），开启时第一个顶级 children 的路由将被替换成接口请求回来的路由数据
 * @description 各字段请查看 `/@/views/system/menu/component/addMenu.vue 下的 ruleForm`
 * @returns 返回路由菜单数据
 */
export const dynamicRoutes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: '/',
		component: () => import('/@common/layout/index.vue'),
		redirect: '/chat',
		meta: {
			isKeepAlive: false,
		},
		children: [
			{
				path: '/chat/:appId?/:conversationId?/:dialogueId?',
				name: 'chat',
				component: () => import('/@/views/chat/index.vue'),
				meta: {
					title: 'common.router.home',
					isLink: '',
					isHide: true,
					isKeepAlive: false,
					isAffix: false,
					isIframe: false,
					roles: ['admin', 'common'],
					icon: 'cool-dashboard-line',
				},
			},
			{
				path: '/agentCenter/:type?',
				name: 'agentCenter',
				component: () => import('/@/views/agent/center/index.vue'),
				meta: {
					title: 'common.router.center',
					isLink: '',
					isHide: true,
					isKeepAlive: false,
					isAffix: false,
					isIframe: false,
					roles: ['admin', 'common'],
					icon: 'cool-dashboard-line',
					// bodyClass: 'agent-center-page'
				},
			},
			{
				path: '/assistant/:conversationId?',
				name: 'assistant',
				component: () => import('/@/views/app/home/index.vue'),
				meta: {
					title: 'common.router.home',
					isLink: '',
					isHide: true,
					isKeepAlive: false,
					isAffix: false,
					isIframe: false,
					roles: ['admin', 'common'],
					icon: 'cool-dashboard-line',
					//bodyClass: 'aiSerach-center-page',
				},
			},
			{
				path: '/aiSearch/:conversationId?',
				name: 'aiSearch',
				component: () => import('/@/views/aiSearch/index.vue'),
				meta: {
					title: 'common.router.aisearch',
					isLink: '',
					isHide: true,
					isKeepAlive: false,
					isAffix: false,
					isIframe: false,
					roles: ['admin', 'common'],
					icon: 'cool-dashboard-line',
					bodyClass: 'aiSerach-center-page'
				},
			},

			{
				path: '/personal',
				name: 'personal',
				component: () => import('/@common/layout/index.vue'),
				redirect: '/personal/overview',
				meta: {
					title: 'common.router.profile',
					isLink: '',
					isHide: false,
					isKeepAlive: true,
					isAffix: false,
					isIframe: false,
					isManage: true,
					roles: ['admin', 'common'],
					icon: 'iconfont icon-xitongshezhi',
				},
				children: [
					{
						path: '/personal/overview',
						name: 'personalOverview',
						component: () => import('/@/views/personal/overview/index.vue'),
						meta: {
							title: 'common.router.accountOverview',
							isLink: '',
							isHide: false,
							isKeepAlive: false,
							isAffix: false,
							isIframe: false,
							isManage: true,
							roles: ['admin', 'common'],
							icon: 'cool-gerenzhongxin',
						},
					},
					{
						path: '/personal/voucher',
						name: 'personalVoucher',
						component: () => import('/@/views/personal/voucher/voucherPage.vue'),
						meta: {
							title: 'common.router.rechargeManagement',
							isLink: '',
							isHide: false,
							isKeepAlive: false,
							isAffix: false,
							isIframe: false,
							isManage: true,
							roles: ['admin', 'common'],
							icon: 'cool-chongzhi',
						},
						children: [
							{
								path: '/personal/voucherPage',
								name: 'voucherPage',
								component: () => import('/@/views/personal/voucher/voucherPage.vue'),
								meta: {
									title: 'common.router.accountRecharge',
									isLink: '',
									isHide: false,
									isKeepAlive: false,
									isAffix: false,
									isIframe: false,
									isManage: true,
									roles: ['admin', 'common'],
									icon: 'cool-gerenzhongxin',
								},
							},
							{
								path: '/personal/voucherList',
								name: 'voucherList',
								component: () => import('/@/views/personal/voucher/voucherList.vue'),
								meta: {
									title: 'common.router.rechargeDetails',
									isLink: '',
									isHide: false,
									isKeepAlive: false,
									isAffix: false,
									isIframe: false,
									isManage: true,
									roles: ['admin', 'common'],
									icon: 'cool-gerenzhongxin',
								},
							},
						],
					},
					{
						path: '/personal/consumptionDetail',
						name: 'personalConsumptionDetail',
						component: () => import('/@/views/personal/consumptionDetail/index.vue'),
						meta: {
							title: 'common.router.expenseDetails',
							isLink: '',
							isHide: false,
							isKeepAlive: false,
							isAffix: false,
							isIframe: false,
							isManage: true,
							roles: ['admin', 'common'],
							icon: 'cool-xiaofeizhongxin',
						},
					},
					{
						path: '/personal/statistic',
						name: 'personalStatistic',
						component: () => import('/@/views/personal/statistic/index.vue'),
						meta: {
							title: 'common.router.callStatistics',
							isLink: '',
							isHide: false,
							isKeepAlive: false,
							isAffix: false,
							isIframe: false,
							isManage: true,
							roles: ['admin', 'common'],
							icon: 'cool-tiaoyongtongji',
						},
					},
					{
						path: '/personal/accountSetting',
						name: 'personalAccountSetting',
						component: () => import('/@/views/personal/accountSetting/index.vue'),
						meta: {
							title: 'common.router.accountSettings',
							isLink: '',
							isHide: false,
							isKeepAlive: false,
							isAffix: false,
							isIframe: false,
							isManage: true,
							roles: ['admin', 'common'],
							icon: 'cool-zhanghaoshezhi',
						},
					},
				],
			},
		],
	},

	
];

/**
 * 定义404、401界面
 * @link 参考：https://next.router.vuejs.org/zh/guide/essentials/history-mode.html#netlify
 */
export const notFoundAndNoPower = [
	{
		path: '/:path(.*)*',
		name: 'notFound',
		component: () => import('/@common/views/error/404.vue'),
		meta: {
			title: 'message.staticRoutes.notFound',
			isHide: true,
		},
	},
];

/**
 * 定义静态路由（默认路由）
 * 此路由不要动，前端添加路由的话，请在 `dynamicRoutes 数组` 中添加
 * @description 前端控制直接改 dynamicRoutes 中的路由，后端控制不需要修改，请求接口路由数据时，会覆盖 dynamicRoutes 第一个顶级 children 的内容（全屏，不包含 layout 中的路由出口）
 * @returns 返回路由菜单数据
 */
export const staticRoutes: Array<RouteRecordRaw> = [
	{
		path: '/login',
		name: 'login',
		component: () => import('/@/views/h5/login/Login.vue'),
		meta: {
			title: '登录',
		},
	},
	{
		path: '/cooperation',
		name: 'cooperation',
		component: () => import('/@common/views/cooperation/index.vue'),
		meta: {
			title: 'common.router.trialApplication',       
		},
	},
	{
		path: '/serviceAgreement',
		name: 'serviceAgreement',
		component: () => import('/@common/views/readFile/index.vue'),
		meta: {
			title: 'common.router.ServiceAgreement',
		},
	},
	{
		path: '/privacyPolicy',
		name: 'privacyPolicy',
		component: () => import('/@common/views/readFile/index.vue'),
		meta: {
			title: 'common.router.privacyPolicy',
		},
	},
	{
		path: '/share/:shareId?/:type?',
		name: 'share',
		component: () => import('/@common/views/share/index.vue'),
		meta: {
			title: 'common.router.share',
		},
	},
	{
		path: '/chath5/:appId?/:conversationId?',
		name: 'chath5',
		component: () => import('/@/views/chath5/index.vue'),
		meta: {
			title: 'common.router.globalTitle',
		},
	},
	{
		path: '/shareAgent/:shareAgentId?',
		name: 'shareAgent',
		component: () => import('/@/views/shareAgent/index.vue'),
		meta: {
			title: 'common.router.share',
		},
	},
	{
		path: '/chatHistory',
		name: 'chatHistory',
		component: () => import('/@/views/chatHistory/index.vue'),
		meta: {
			title: 'common.router.home',
			isLink: '',
			isHide: true,
			isKeepAlive: false,
			isAffix: false,
			isIframe: false,
			roles: ['admin', 'common'],
			icon: 'cool-dashboard-line',
			bodyClass: 'aiSerach-center-page'
		},
	},
	{
		path: '/agentpub/:appId?/:ticket?/:conversationId?/:dialogueId?',
		name: 'agentpub',
		component: () => import('/@/views/agentpub/index.vue'),
		meta: {
			title: 'common.router.publick',
			bodyClass: 'agentPub-center-page'
		},
	},
	{
		path: '/checkLogs/:appId?/:conversationId?/:dialogueId?',
		name: 'checkLogs',
		component: () => import('/@/views/checkLogs/index.vue'),
		meta: {
			title: 'common.router.QAndA',
			bodyClass: 'checklogs-center-page'
		},
	},
	/**
	 * 提示：写在这里的为全屏界面，不建议写在这里
	 * 请写在 `dynamicRoutes` 路由数组中
	 */
];
